import { EventKind } from '../sim/types';
import type { SimEvent } from '../sim/types';
import { warheadDef } from '../content/combat';
import { buildVoice } from './synth';
import { SOUND_BY_ID, SOUND_IDS } from './sounds';
import type { SoundDef, SoundId } from './sounds';

/*
 * מנוע הצליל — ה-API הציבורי היחיד של המודול.
 *
 * שלושה כללים מחזיקים את הקובץ הזה:
 *
 * 1. **הצליל הוא פלט של התצוגה, לא של המשחק.** בדיוק כמו `render/effects.ts`,
 *    הוא נבנה מ-`state.events` בכיוון אחד ואף פעם לא חוזר לסימולציה. לכן מותר
 *    כאן `Math.random`, `localStorage` ושעון אמת — שום דבר מזה אינו נכנס ל-hash.
 *
 * 2. **קרב אמיתי הוא מבול.** עשרות אירועים בטיק. בלי תקרת קולות וחלון זמן, ארבעים
 *    יריות מתנגנות יחד, נשמעות כמו רעש לבן, ומחניקות את הדף. המדיניות יושבת
 *    בדאטה (`sounds.ts`) ולא בקוד — כדי שאיזון יהיה עריכת מספר.
 *
 * 3. **כל צומת שנוצר חייב למות.** כל מקור מתוזמן ל-`stop` מראש, ומנותק ב-`onended`
 *    (עם טיימר גיבוי, למקרה שהאירוע לא יגיע). דליפה כאן נראית כמו "המשחק נתקע
 *    אחרי דקה".
 */

export type { SoundId, SoundDef } from './sounds';
export { SOUND_IDS, SOUND_DEFS, SOUNDS, SOUND_BY_ID, soundDef } from './sounds';

const STORAGE_KEY = 'cf_audio';

/**
 * תקרה גלובלית, מעל תקרת הקולות של כל צליל בנפרד. פיצוץ המוני יכול לעבור את
 * המכסה של כמה צלילים שונים בו-זמנית ועדיין לחנוק את המעבד.
 */
const MAX_TOTAL_VOICES = 20;

interface Prefs {
    muted: boolean;
    volume: number;
}

let ctx: AudioContext | null = null;
/** עוצמת המאסטר — הנקודה היחידה שההשתקה נוגעת בה. */
let master: GainNode | null = null;
/** מה שכל הקולות מתחברים אליו (הקומפרסור, אם קיים). */
let bus: AudioNode | null = null;
let unlocked = false;

const prefs: Prefs = loadPrefs();

/** מתי הושמע כל צליל לאחרונה, בשעון ההקשר. */
const lastPlayedAt = new Map<string, number>();
/** כמה קולות של כל צליל מתנגנים כרגע. */
const activeVoices = new Map<string, number>();
let totalVoices = 0;

// ---------------------------------------------------------------- מחזור חיים

/**
 * יוצר את ה-AudioContext. אפשר לקרוא כמה פעמים שרוצים.
 *
 * ההקשר נולד `suspended` בכל דפדפן מודרני, ולכן היצירה לבדה אינה מספיקה —
 * ראו `resumeAudio`.
 */
export function initAudio(): void {
    if (ctx) return;
    const Ctor = audioContextCtor();
    if (!Ctor) return;

    try {
        const created = new Ctor();
        const gain = created.createGain();
        gain.gain.value = prefs.muted ? 0 : prefs.volume;
        gain.connect(created.destination);

        // קומפרסור על המאסטר: מה שמונע ממטח בו-זמני לקרוע את הרמקול. לא כל
        // סביבה מממשת אותו, ולכן הוא אופציונלי ולא תנאי.
        let head: AudioNode = gain;
        if (typeof created.createDynamicsCompressor === 'function') {
            const comp = created.createDynamicsCompressor();
            comp.threshold.value = -18;
            comp.knee.value = 20;
            comp.ratio.value = 8;
            comp.attack.value = 0.003;
            comp.release.value = 0.2;
            comp.connect(gain);
            head = comp;
        }

        ctx = created;
        master = gain;
        bus = head;
    } catch {
        // דפדפן שחוסם אודיו לגמרי אינו סיבה להפיל משחק שרץ.
        ctx = null;
        master = null;
        bus = null;
    }
}

/**
 * מחדש את ההקשר. **חייב להיקרא מתוך אירוע משתמש אמיתי** (pointerdown/keydown),
 * ובטוח לקריאה חוזרת — כל מגע יכול לקרוא לו.
 *
 * ב-iOS Safari `resume()` לבדו לפעמים אינו מספיק וההקשר נשאר `suspended` עד
 * שמתנגן מאגר כלשהו. לכן יש כאן מאגר שקט של פריים אחד כגיבוי.
 */
export async function resumeAudio(): Promise<void> {
    initAudio();
    const current = ctx;
    if (!current) return;
    try {
        if (current.state !== 'running') await current.resume();
        if (current.state !== 'running' && !unlocked) {
            unlocked = true;
            playSilentFrame(current);
            await current.resume();
        }
    } catch {
        // הדפדפן סירב. המשחק ממשיך בשקט.
    }
}

/** משחרר את כל המשאבים. נקרא ביציאה ממשחק, ומאפס את המנוע לגמרי. */
export function disposeAudio(): void {
    const current = ctx;
    ctx = null;
    master = null;
    bus = null;
    unlocked = false;
    lastPlayedAt.clear();
    activeVoices.clear();
    totalVoices = 0;
    if (!current) return;
    try {
        void current.close();
    } catch {
        // הקשר שכבר נסגר.
    }
}

// ---------------------------------------------------------------- העדפות

export function setMuted(muted: boolean): void {
    prefs.muted = muted;
    applyMasterGain();
    savePrefs();
}

export function isMuted(): boolean {
    return prefs.muted;
}

/** 0..1. ערך מחוץ לתחום נחתך. */
export function setVolume(v: number): void {
    prefs.volume = clamp01(v);
    applyMasterGain();
    savePrefs();
}

export function getVolume(): number {
    return prefs.volume;
}

// ---------------------------------------------------------------- השמעה

export interface PlayOptions {
    /** ‎-1 שמאל, 0 מרכז, 1 ימין. מחוץ לתחום נחתך. */
    readonly pan?: number;
    /** מכפיל עוצמה נוסף לצליל הזה בלבד, 0..1. */
    readonly volume?: number;
}

export function playSound(id: SoundId, options?: PlayOptions): void {
    // ההשתקה עוצרת **לפני** יצירת צמתים ולא רק מנמיכה את המאסטר: משחק מושתק
    // לא אמור לשלם על סינתזה שאיש לא ישמע.
    if (prefs.muted) return;

    const def: SoundDef | undefined = SOUND_BY_ID[id];
    if (!def) return;

    initAudio();
    const current = ctx;
    const out = bus;
    if (!current || !out) return;
    // הקשר מושהה = הדפדפן עוד לא קיבל מגע. תזמון קולות עכשיו רק היה מצטבר
    // ומתפוצץ כולו יחד ברגע ה-resume.
    if (current.state !== 'running') return;

    const now = current.currentTime;

    const last = lastPlayedAt.get(id);
    if (last !== undefined && (now - last) * 1000 < def.minGapMs) return;

    const active = activeVoices.get(id) ?? 0;
    if (active >= def.maxVoices) return;
    if (totalVoices >= MAX_TOTAL_VOICES) return;

    let destination: AudioNode = out;
    const extra: AudioNode[] = [];
    const pan = options?.pan;
    if (pan !== undefined && typeof current.createStereoPanner === 'function') {
        const panner = current.createStereoPanner();
        panner.pan.value = clampPan(pan);
        panner.connect(out);
        extra.push(panner);
        destination = panner;
    }

    const jitter = def.pitchJitter;
    const pitch = jitter > 0 ? 1 + (Math.random() * 2 - 1) * jitter : 1;
    const gain = def.gain * clamp01(options?.volume ?? 1);

    const voice = buildVoice(current, destination, def.layers, { startAt: now, pitch, gain });

    activeVoices.set(id, active + 1);
    lastPlayedAt.set(id, now);
    totalVoices++;

    const nodes = [...voice.nodes, ...extra];
    let pending = voice.sources.length;
    let released = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const release = (): void => {
        if (released) return;
        released = true;
        if (timer !== null) clearTimeout(timer);
        for (const node of nodes) {
            try {
                node.disconnect();
            } catch {
                // צומת שכבר נותק.
            }
        }
        activeVoices.set(id, Math.max(0, (activeVoices.get(id) ?? 1) - 1));
        totalVoices = Math.max(0, totalVoices - 1);
    };

    if (pending === 0) {
        release();
        return;
    }

    // רשת ביטחון: אם `onended` לא יגיע (קרה בדפדפנים ובסביבות בדיקה), הספירה
    // הייתה נתקעת והצליל היה משתתק לנצח.
    timer = setTimeout(release, Math.max(0, (voice.endsAt - now) * 1000) + 500);

    for (const source of voice.sources) {
        source.onended = () => {
            pending--;
            if (pending <= 0) release();
        };
    }
}

// ---------------------------------------------------------------- אירועי טיק

export interface ConsumeOptions {
    /**
     * אירוע הירייה **אינו נושא את זהות היורה** — הסימולציה שולחת רק מיקום וכיוון.
     * מי שמחווט יכול להעביר כאן פונקציה שמסתכלת במצב המשחק ומחזירה 'heavy' לירי
     * תותח. בלי זה כל ירייה נשמעת כירי קל.
     */
    readonly shotWeight?: (event: SimEvent) => 'light' | 'heavy';
    /**
     * גם אירוע המוות אינו מבדיל בין יחידה למבנה (`a` הוא אינדקס טיפוס בשתי
     * הטבלאות הנפרדות). מי שמחווט מכריע.
     */
    readonly isBuildingDeath?: (event: SimEvent) => boolean;
    /** האם האירוע שייך לשחקן המקומי. משמש לאירועים שנוגעים רק לו. */
    readonly isLocal?: (event: SimEvent) => boolean;
}

/** לאיזה צליל מתורגם אירוע בודד, או null אם הוא שקט (קליע שהחטיא). */
export function soundForEvent(event: SimEvent, options?: ConsumeOptions): SoundId | null {
    switch (event.kind) {
        case EventKind.Shot:
            return options?.shotWeight?.(event) === 'heavy' ? 'shotHeavy' : 'shotLight';
        case EventKind.Impact:
            // b=0 הוא קליע שאיבד את מטרתו ונפל לריק. גם האפקטים מתעלמים ממנו.
            if (event.b !== 1) return null;
            return warheadDef(event.a).effect === 'blast' ? 'impactHeavy' : 'impactLight';
        case EventKind.Death:
            return options?.isBuildingDeath?.(event) ? 'deathBuilding' : 'deathUnit';
        case EventKind.Spawned:
            // רק היחידות שלי — צליל "מוכן" על כל טנק של היריב הוא רעש, לא מידע.
            return options?.isLocal?.(event) ? 'unitReady' : null;
        default:
            return null;
    }
}

/**
 * צורך את אירועי הטיק ומשמיע אותם. `panOf` ממיר מיקום עולם למיקום סטריאו
 * ‎(-1..1) — בדרך כלל לפי המרחק ממרכז המסך.
 */
export function consumeEvents(
    events: readonly SimEvent[],
    panOf: (x: number, y: number) => number,
    options?: ConsumeOptions,
): void {
    if (prefs.muted) return;
    for (const event of events) {
        const id = soundForEvent(event, options);
        if (id === null) continue;
        playSound(id, { pan: panOf(event.x, event.y) });
    }
}

/** עוזר נוח: ממיר מיקום עולם ל-pan לפי רוחב התצוגה. */
export function panFromScreenX(screenX: number, viewportWidth: number): number {
    if (viewportWidth <= 0) return 0;
    return clampPan((screenX / viewportWidth) * 2 - 1);
}

// ---------------------------------------------------------------- פנימי

function applyMasterGain(): void {
    const gain = master;
    const current = ctx;
    if (!gain || !current) return;
    const target = prefs.muted ? 0 : prefs.volume;
    try {
        // מדרגה חדה בעוצמה נשמעת כקליק. 15ms מספיקים כדי להעלים אותו.
        gain.gain.setValueAtTime(gain.gain.value, current.currentTime);
        gain.gain.linearRampToValueAtTime(target, current.currentTime + 0.015);
    } catch {
        gain.gain.value = target;
    }
}

function playSilentFrame(current: AudioContext): void {
    try {
        const buffer = current.createBuffer(1, 1, current.sampleRate > 0 ? current.sampleRate : 44100);
        const source = current.createBufferSource();
        source.buffer = buffer;
        source.connect(current.destination);
        source.start(0);
        source.stop(current.currentTime + 0.01);
        source.onended = () => {
            try {
                source.disconnect();
            } catch {
                // כבר נותק.
            }
        };
    } catch {
        // סביבה בלי תמיכה. לא נורא.
    }
}

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
    if (typeof globalThis === 'undefined') return null;
    const scope = globalThis as unknown as {
        AudioContext?: AudioContextCtor;
        webkitAudioContext?: AudioContextCtor;
    };
    return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

function loadPrefs(): Prefs {
    const fallback: Prefs = { muted: false, volume: 0.7 };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return fallback;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return fallback;
        const record = parsed as { muted?: unknown; volume?: unknown };
        return {
            muted: typeof record.muted === 'boolean' ? record.muted : fallback.muted,
            volume: typeof record.volume === 'number' ? clamp01(record.volume) : fallback.volume,
        };
    } catch {
        // מצב פרטי חוסם localStorage, ו-JSON פגום קורה. ברירת מחדל ולא קריסה.
        return fallback;
    }
}

function savePrefs(): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        // אין אחסון — ההעדפה תחיה עד סוף הסשן, וזה מספיק.
    }
}

function clamp01(value: number): number {
    if (!Number.isFinite(value)) return 0;
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
}

function clampPan(value: number): number {
    if (!Number.isFinite(value)) return 0;
    if (value < -1) return -1;
    if (value > 1) return 1;
    return value;
}

/** מזהי הצלילים כטיפוס בזמן ריצה — שימושי לבורר בממשק. */
export function isSoundId(value: string): value is SoundId {
    return (SOUND_IDS as readonly string[]).includes(value);
}
